import { memo } from 'react';
import { Check } from 'lucide-react';
import { getLocalizedStepField } from '@/lib/utils';
import type { PhotoStep } from '@transpordiamet/shared';

interface StepSidebarProps {
  steps: PhotoStep[];
  currentStepId: string | undefined;
  completedStepIds: Set<string>;
  locale: string;
  activeStepRef?: React.RefObject<HTMLDivElement | null>;
  stepsContainerRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Sidebar component showing list of all steps in the photo capture workflow
 * Memoized to prevent rerenders when parent state changes
 */
export const StepSidebar = memo<StepSidebarProps>(
  ({ steps, currentStepId, completedStepIds, locale, activeStepRef, stepsContainerRef }) => {
    return (
      <div className="bg-gray-50 px-2 py-2 min-w-0 flex flex-col h-full min-h-0">
        <div ref={stepsContainerRef} className="space-y-1 overflow-y-auto flex-1 min-h-0">
          {steps.map((step, index) => {
            const isActive = step.id === currentStepId;
            const isCompleted = completedStepIds.has(step.id);
            const stepTitle = getLocalizedStepField(step, 'title', locale);

            return (
              <div
                key={step.id}
                ref={isActive ? activeStepRef : undefined}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left ${
                  isActive
                    ? 'bg-blue-100 border border-blue-500'
                    : isCompleted
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-white border border-gray-200 opacity-60'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                    isActive
                      ? 'bg-blue-500 text-white'
                      : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : index + 1}
                </div>

                <span
                  className={`flex-1 min-w-0 text-sm font-medium truncate ${isActive ? 'text-blue-900' : 'text-gray-900'}`}
                >
                  {stepTitle}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.currentStepId === nextProps.currentStepId &&
    prevProps.completedStepIds === nextProps.completedStepIds &&
    prevProps.locale === nextProps.locale &&
    prevProps.steps === nextProps.steps
);

StepSidebar.displayName = 'StepSidebar';
